import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Section from '../shared/Section';
import SectionHeading from '../shared/SectionHeading';
import './Partner.css';

const INITIAL_FORM = {
  name: '',
  phone: '',
  email: '',
};

const API_ENDPOINT = 'https://abc1234.execute-api.us-east-1.amazonaws.com/01/contact';

const phonePattern = /^\+?[0-9\s-]{8,15}$/;
const emailPattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/;

const Partner = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: null, message: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setErrors({});
  };

  const validate = useMemo(
    () => (values) => {
      const nextErrors = {};

      if (!values.name || values.name.trim().length < 2) {
        nextErrors.name = 'Please enter at least two characters.';
      }
      if (!values.phone || !phonePattern.test(values.phone.trim())) {
        nextErrors.phone = 'Please enter a valid phone number.';
      }
      if (!values.email || !emailPattern.test(values.email.trim())) {
        nextErrors.email = 'Please enter a valid email address.';
      }

      return nextErrors;
    },
    []
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const nextForm = { ...form, [name]: value };
    setForm(nextForm);
    setErrors(validate(nextForm));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: '' });

    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setStatus({
        type: 'success',
        message: 'Thanks! Our partnerships team will reach out shortly.',
      });
      resetForm();
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'We could not send your request right now. Please try again in a moment.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section
      background='linear-gradient(180deg, rgba(246, 120, 95, 0.08) 0%, rgba(255, 255, 255, 1) 55%)'
    >
      <Stack spacing={6} alignItems='center' textAlign='center'>
        <SectionHeading
          eyebrow='Get Your Proposal'
          title='Ready to upgrade your workplace dining experience?'
          description='Join the companies saving money and the planet. Deploy FoodFe for tastier meals and smarter operations.'
        />
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={() => {
            setDialogOpen(true);
            setStatus({ type: null, message: '' });
            setErrors({});
          }}
        >
          Get Your Proposal
        </Button>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setStatus({ type: null, message: '' });
        }}
        fullWidth
        maxWidth='md'
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, md: 4 },
            width: { xs: 'calc(100% - 32px)', md: 'auto' },
            m: { xs: 2, md: 6 },
            p: 0,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, px: { xs: 2, md: 4 }, pt: { xs: 3, md: 4 } }}>
          Partner with FoodFe
        </DialogTitle>
        <DialogContent
          sx={{
            pt: { xs: 2, md: 3 },
            px: { xs: 2, md: 4 },
            pb: { xs: 3, md: 4 },
          }}
        >
          <Typography variant='body2' color='text.secondary' mb={3}>
            Leave your details and we will set up a conversation within one business day.
          </Typography>
          <Stack component='form' spacing={3} onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label='Name'
                  name='name'
                  value={form.name}
                  onChange={handleInputChange}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Phone number'
                  name='phone'
                  value={form.phone}
                  onChange={handleInputChange}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Work email'
                  name='email'
                  type='email'
                  value={form.email}
                  onChange={handleInputChange}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  fullWidth
                />
              </Grid>
            </Grid>
            {status.type ? (
              <Alert severity={status.type} onClose={() => setStatus({ type: null, message: '' })}>
                {status.message}
              </Alert>
            ) : null}

            <DialogActions sx={{ px: { xs: 2, md: 4 }, pt: 1, pb: { xs: 2, md: 3 } }}>
              <Button
                onClick={() => {
                  setDialogOpen(false);
                  setStatus({ type: null, message: '' });
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                type='submit'
                disabled={
                  isSubmitting ||
                  !form.name.trim() ||
                  !phonePattern.test(form.phone.trim()) ||
                  !emailPattern.test(form.email.trim())
                }
              >
                {isSubmitting ? 'Sending…' : 'Send enquiry'}
              </Button>
            </DialogActions>
          </Stack>
        </DialogContent>
      </Dialog>
    </Section>
  );
};

export default Partner;
